import { useQueryClient, useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import type { FormData, Constante } from '../types/consultation';
import type { Analyse } from '../types/analyse';
import type { Medication } from '../types/medication';
import { initializeFormData } from '../utils/consultationUtils';

const WIZARD_QUERY_KEY = ['consultation-wizard-state'];

export interface WizardState {
    formData: FormData;
    analyses: Analyse[];
    medications: Medication[];
    editAnalyseIndex: number | null;
    editMedicationIndex: number | null;
}

export const useConsultationWizardState = (constanteData?: Constante) => {
    const queryClient = useQueryClient();

    // Initialize or get the current state
    const { data } = useQuery<WizardState>({
        queryKey: WIZARD_QUERY_KEY,
        queryFn: async () => {
            // This queryFn is required by React Query v5
            // Check if data already exists in cache
            const existingData = queryClient.getQueryData<WizardState>(WIZARD_QUERY_KEY);
            if (existingData) {
                return existingData;
            }

            // If no data exists, return initial state
            // This will only run once when the query is first created
            const initialState: WizardState = {
                formData: initializeFormData(constanteData),
                analyses: [],
                medications: [],
                editAnalyseIndex: null,
                editMedicationIndex: null,
            };

            // Set it in cache immediately to prevent re-creation
            queryClient.setQueryData(WIZARD_QUERY_KEY, initialState);
            return initialState;
        },
        staleTime: Infinity,
        gcTime: Infinity,
        // Use placeholderData instead of initialData to avoid re-creation
        placeholderData: {
            formData: initializeFormData(constanteData),
            analyses: [],
            medications: [],
            editAnalyseIndex: null,
            editMedicationIndex: null,
        },
    });

    const state = data as WizardState;

    const setState = useCallback((updater: (prev: WizardState) => WizardState) => {
        queryClient.setQueryData(WIZARD_QUERY_KEY, updater);
    }, [queryClient]);

    const updateFormData = useCallback((
        section: keyof FormData,
        fieldOrValue: any,
        value?: any
    ) => {
        setState((prev) => {
            const isRootField = value === undefined;

            if (isRootField) {
                return {
                    ...prev,
                    formData: { ...prev.formData, [section]: fieldOrValue }
                };
            }

            const sectionData = (prev.formData as any)[section];
            if (typeof sectionData === "object" && sectionData !== null) {
                return {
                    ...prev,
                    formData: {
                        ...prev.formData,
                        [section]: { ...sectionData, [fieldOrValue]: value },
                    }
                };
            }

            return prev;
        });
    }, [setState]);

    const handleAddOrEditAnalyse = useCallback((analyse: Analyse) => {
        setState((prev) => {
            if (prev.editAnalyseIndex !== null) {
                return {
                    ...prev,
                    analyses: prev.analyses.map((a, i) => (i === prev.editAnalyseIndex ? analyse : a)),
                    editAnalyseIndex: null,
                };
            }
            return { ...prev, analyses: [...prev.analyses, analyse] };
        });
    }, [setState]);

    const handleAddOrEditMedication = useCallback((med: Medication) => {
        setState((prev) => {
            if (prev.editMedicationIndex !== null) {
                return {
                    ...prev,
                    medications: prev.medications.map((m, i) => (i === prev.editMedicationIndex ? med : m)),
                    editMedicationIndex: null,
                };
            }
            return { ...prev, medications: [...prev.medications, med] };
        });
    }, [setState]);

    const resetWizard = useCallback(() => {
        queryClient.setQueryData(WIZARD_QUERY_KEY, {
            formData: initializeFormData(constanteData),
            analyses: [],
            medications: [],
            editAnalyseIndex: null,
            editMedicationIndex: null,
        });
    }, [queryClient, constanteData]);

    const setEditAnalyseIndex = useCallback((index: number | null) => {
        setState(prev => ({ ...prev, editAnalyseIndex: index }));
    }, [setState]);

    const setEditMedicationIndex = useCallback((index: number | null) => {
        setState(prev => ({ ...prev, editMedicationIndex: index }));
    }, [setState]);

    // Expose everything needed by the steps
    return {
        ...state,
        updateFormData,
        handleAddOrEditAnalyse,
        handleAddOrEditMedication,
        setEditAnalyseIndex,
        setEditMedicationIndex,
        setFormData: (updater: FormData | ((prev: FormData) => FormData)) =>
            setState(prev => ({
                ...prev,
                formData: typeof updater === 'function' ? updater(prev.formData) : updater
            })),
        setAnalyses: (updater: Analyse[] | ((prev: Analyse[]) => Analyse[])) =>
            setState(prev => ({
                ...prev,
                analyses: typeof updater === 'function' ? updater(prev.analyses) : updater
            })),
        setMedications: (updater: Medication[] | ((prev: Medication[]) => Medication[])) =>
            setState(prev => ({
                ...prev,
                medications: typeof updater === 'function' ? updater(prev.medications) : updater
            })),
        resetWizard,
    };
};
